import { useTOptions, VALIDATION_ERROR_KEYS } from "@/i18n"
import { DataDrivenFormSchema } from "@/lib/data-driven-forms/schema"
import * as z from "zod"
import { useFormProps } from "./page-props"
import { UPLOAD_FILE_TYPE } from "@/constants/ui"
import { attachmentSchema } from "@/lib/zod-schemas/attachment-schema"

// Helper schemas for reusable patterns
const requiredString = z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED)
const boolean = z
  .union([z.literal("true"), z.literal("false"), z.boolean(), z.null()])
  .transform((val) => {
    if (val === null) return false
    if (typeof val === "boolean") return val
    return val === "true"
  })

// Other information schema for medical/learning disabilities
const otherInformationSchema = z.object({
  eyesight: z.string().nullable().optional(),
  eyesight_professional_diagnosis: boolean.nullable().optional(),
  eyesight_comment: z.string().nullable().optional(),
  adhd: z.string().nullable().optional(),
  adhd_professional_diagnosis: boolean.nullable().optional(),
  adhd_comment: z.string().nullable().optional(),
  autistic: z.string().nullable().optional(),
  autistic_professional_diagnosis: boolean.nullable().optional(),
  autistic_comment: z.string().nullable().optional(),
  depression: z.string().nullable().optional(),
  depression_professional_diagnosis: boolean.nullable().optional(),
  depression_comment: z.string().nullable().optional(),
  other_comment: z.string().nullable().optional(),
})

// Interested subjects attributes schema
const interestedSubjectsSchema = z.object({
  subject_id: z.number({
    error: VALIDATION_ERROR_KEYS.REQUIRED,
  }),
  qualitative_assessment: z.string().nullable(),
})

// GAT (General Achievement Test) attributes schema
const gatAttributesSchema = z.object({
  writing_met_standard_score: boolean,
  writing_achieved_excellence: boolean,
  reading_met_standard_score: boolean,
  reading_achieved_excellence: boolean,
  numeracy_met_standard_score: boolean,
  numeracy_achieved_excellence: boolean,
  written_score: z.string().nullable(),
  reading_score: z.string().nullable(),
  numeracy_score: z.string().nullable(),
})

// WISC-V (Wechsler Intelligence Scale for Children) attributes schema
const wiscVAttributesSchema = z.object({
  exam_id: z.string().nullable(),
  test_date: z.string().nullable(),
  report_date: z.string().nullable(),
  grade: z.string().nullable(),
  handedness: z.string().nullable(),
  examiner_name: z.string().nullable(),
  fsiq_score: z.coerce.number().nullable(),
  fsiq_percentile: z.coerce.number().nullable(),
  fri_score: z.coerce.number().nullable(),
  fri_percentile: z.coerce.number().nullable(),
  vci_score: z.coerce.number().nullable(),
  vci_percentile: z.coerce.number().nullable(),
  vsi_score: z.coerce.number().nullable(),
  vsi_percentile: z.coerce.number().nullable(),
  wmi_score: z.coerce.number().nullable(),
  wmi_percentile: z.coerce.number().nullable(),
  psi_score: z.coerce.number().nullable(),
  psi_percentile: z.coerce.number().nullable(),
})

// WIAT-IV (Wechsler Individual Achievement Test) attributes schema
const wiatIvAttributesSchema = z.object({
  exam_id: z.string().nullable(),
  test_date: z.string().nullable(),
  report_date: z.string().nullable(),
  age_at_test: z.string().nullable(),
  grade: z.string().nullable(),
  semester: z.string().nullable(),
  handedness: z.string().nullable(),
  is_retest: boolean,
  examiner_name: z.string().nullable(),
  total_achievement_composite_score: z.coerce.number().nullable(),
  total_achievement_composite_percentile: z.coerce.number().nullable(),
  word_reading_score: z.coerce.number().nullable(),
  word_reading_percentile: z.coerce.number().nullable(),
  reading_comprehension_score: z.coerce.number().nullable(),
  reading_comprehension_percentile: z.coerce.number().nullable(),
  spelling_score: z.coerce.number().nullable(),
  spelling_percentile: z.coerce.number().nullable(),
  sentence_composition_score: z.coerce.number().nullable(),
  sentence_composition_percentile: z.coerce.number().nullable(),
  math_problem_solving_score: z.coerce.number().nullable(),
  math_problem_solving_percentile: z.coerce.number().nullable(),
  numerical_operations_score: z.coerce.number().nullable(),
  numerical_operations_percentile: z.coerce.number().nullable(),
  reading_composite_score: z.coerce.number().nullable(),
  reading_composite_percentile: z.coerce.number().nullable(),
  written_expression_composite_score: z.coerce.number().nullable(),
  written_expression_composite_percentile: z.coerce.number().nullable(),
  mathematics_composite_score: z.coerce.number().nullable(),
  mathematics_composite_percentile: z.coerce.number().nullable(),
  basic_reading_composite_score: z.coerce.number().nullable(),
  basic_reading_composite_percentile: z.coerce.number().nullable(),
  decoding_composite_score: z.coerce.number().nullable(),
  decoding_composite_percentile: z.coerce.number().nullable(),
  reading_fluency_composite_score: z.coerce.number().nullable(),
  reading_fluency_composite_percentile: z.coerce.number().nullable(),
  math_fluency_composite_score: z.coerce.number().nullable(),
  math_fluency_composite_percentile: z.coerce.number().nullable(),
  writing_fluency_composite_score: z.coerce.number().nullable(),
  writing_fluency_composite_percentile: z.coerce.number().nullable(),
  oral_language_composite_score: z.coerce.number().nullable(),
  oral_language_composite_percentile: z.coerce.number().nullable(),
  phonological_processing_composite_score: z.coerce.number().nullable(),
  phonological_processing_composite_percentile: z.coerce.number().nullable(),
  orthographic_processing_composite_score: z.coerce.number().nullable(),
  orthographic_processing_composite_percentile: z.coerce.number().nullable(),
  orthographic_processing_extended_composite_score: z.coerce
    .number()
    .nullable(),
  orthographic_processing_extended_composite_percentile: z.coerce
    .number()
    .nullable(),
  dyslexia_index_composite_score: z.coerce.number().nullable(),
  dyslexia_index_composite_percentile: z.coerce.number().nullable(),
})

// Ability attributes schema
const abilityAttributesSchema = z.object({
  overall_qualitative_assessment: z.string().nullable().optional(),
  qualitative_assessment_comment: z.string().nullable().optional(),
  interested_subjects_attributes: z.array(interestedSubjectsSchema).optional(),
  naplan_score_numeracy: z.string().nullable().optional(),
  naplan_score_reading: z.string().nullable().optional(),
  naplan_score_writing: z.string().nullable().optional(),
  naplan_score_spelling: z.string().nullable().optional(),
  naplan_score_grammar: z.string().nullable().optional(),
  gat_attributes: gatAttributesSchema.optional(),
  wais_iv_full_scale_iq_index: z.coerce.number().nullable().optional(),
  wais_iv_full_scale_iq_percentile_rank: z.coerce
    .number()
    .nullable()
    .optional(),
  wais_iv_general_ability_index: z.coerce.number().nullable().optional(),
  wais_iv_general_ability_percentile_rank: z.coerce
    .number()
    .nullable()
    .optional(),
  wais_iv_verbal_comprehension_index: z.coerce.number().nullable().optional(),
  wais_iv_verbal_comprehension_percentile_rank: z.coerce
    .number()
    .nullable()
    .optional(),
  wais_iv_perceptual_reasoning_index: z.coerce.number().nullable().optional(),
  wais_iv_perceptual_reasoning_percentile_rank: z.coerce
    .number()
    .nullable()
    .optional(),
  wais_iv_working_memory_index: z.coerce.number().nullable().optional(),
  wais_iv_working_memory_percentile_rank: z.coerce
    .number()
    .nullable()
    .optional(),
  wais_iv_processing_speed_index: z.coerce.number().nullable().optional(),
  wais_iv_processing_speed_percentile_rank: z.coerce
    .number()
    .nullable()
    .optional(),
  wisc_v_attributes: wiscVAttributesSchema.optional(),
  wiat_iv_attributes: wiatIvAttributesSchema.optional(),
  aeas_score_reading: z.coerce.number().nullable().optional(),
  aeas_score_spelling: z.coerce.number().nullable().optional(),
  aeas_score_written_essay: z.coerce.number().nullable().optional(),
  aeas_score_listening: z.coerce.number().nullable().optional(),
  aeas_score_speaking: z.coerce.number().nullable().optional(),
  aeas_score_overall: z.coerce.number().nullable().optional(),
})

// Student profile schema
const studentProfileSchema = z.object({
  user_attributes: z.object({
    email: z.email().min(1, { message: VALIDATION_ERROR_KEYS.REQUIRED }),
    first_name: requiredString,
    last_name: requiredString,
    gender: z.string().nullable().optional(),
    religion_id: z.number().nullable().optional(),
    primary_language_id: z.number().nullable().optional(),
    race_id: z.number().nullable().optional(),
    secondary_language_id: z.number().nullable().optional(),
    have_aeas_score: z.boolean().nullable().optional(),
    citizenship_country_ids: z.array(z.number()).optional(),
    origin_country_id: z.number().nullable().optional(),
    dob: z.string().nullable().optional(),
  }),
  parent_one_education: z.string().nullable().optional(),
  parent_one_occupation: z.string().nullable().optional(),
  parent_two_education: z.string().nullable().optional(),
  parent_two_occupation: z.string().nullable().optional(),
  learning_style_description: z.string().nullable().optional(),
  // VARK Learning Styles
  learning_style_visual: z.string().nullable().optional(),
  learning_style_auditory: z.string().nullable().optional(),
  learning_style_read_write: z.string().nullable().optional(),
  learning_style_kinesthetic: z.string().nullable().optional(),
  // Multiple Intelligence Learning Styles
  learning_style_linguistic: z.string().nullable().optional(),
  learning_style_logical: z.string().nullable().optional(),
  learning_style_musical: z.string().nullable().optional(),
  learning_style_spatial: z.string().nullable().optional(),
  learning_style_body_kinesthetic: z.string().nullable().optional(),
  learning_style_interpersonal: z.string().nullable().optional(),
  learning_style_intrapersonal: z.string().nullable().optional(),
  learning_style_naturalistic: z.string().nullable().optional(),
  // Kolb Learning Styles
  learning_style_concrete_exp: z.string().nullable().optional(),
  learning_style_reflective_obs: z.string().nullable().optional(),
  learning_style_abstract_conc: z.string().nullable().optional(),
  learning_style_active_exp: z.string().nullable().optional(),
  // Learning Preferences
  learning_preference_group: boolean.nullable().optional(),
  learning_preference_individual: boolean.nullable().optional(),
  learning_preference_like_students: boolean.nullable().optional(),
  learning_preference_diverse_students: boolean.nullable().optional(),
  learning_preference_teacher_centered: z.number().nullable().optional(),
  learning_preference_structured: z.number().nullable().optional(),
  learning_preference_technology_enhanced: z.number().nullable().optional(),
  learning_preference_by_doing: z.number().nullable().optional(),
  learning_preference_description: z.string().nullable().optional(),
  // Nested attributes
  other_information_attributes: otherInformationSchema.optional(),
  ability_attributes: abilityAttributesSchema.optional(),
})

// Student attachments schema
const studentAttachmentsSchema = z.object({
  photo_id: attachmentSchema,
  naplan_chart: attachmentSchema,
})

export const studentSchema = z.object({
  student: studentProfileSchema,
  student_attachments: studentAttachmentsSchema,
})

export const formSchema: DataDrivenFormSchema<"students"> = {
  type: "form",
  validator: studentSchema,
  namespace: "students",
  properties: [
    {
      type: "section:hash",
      sectionKey: "general_information",
      title: "form.sections.general_information",
      properties: [
        {
          type: "field:attachment",
          accept: UPLOAD_FILE_TYPE.IMAGE,
          name: "student_attachments.photo_id.attachment",
          label: "form.general_information_section.photo_id",
          useAttachment: () => useFormProps().student_attachments.photo_id,
        },
        {
          type: "field:input",
          inputType: "text",
          name: "student.user_attributes.first_name",
          label: "form.general_information_section.first_name",
        },
        {
          type: "field:input",
          inputType: "text",
          name: "student.user_attributes.last_name",
          label: "form.general_information_section.last_name",
        },
        {
          type: "field:input",
          inputType: "email",
          name: "student.user_attributes.email",
          label: "form.general_information_section.email",
        },
        {
          type: "field:input",
          inputType: "text",
          name: "student.user_attributes.dob",
          label: "form.general_information_section.dob",
        },
        {
          type: "field:combobox",
          comboboxType: "single",
          name: "student.user_attributes.gender",
          label: "form.general_information_section.gender",
          useOptions: () => useTOptions("user.gender", "common"),
        },
        {
          type: "field:combobox",
          comboboxType: "single",
          name: "student.user_attributes.religion_id",
          label: "form.general_information_section.religion",
          useOptions: () => useFormProps().religions,
          tooltipContent: "form.general_information_section.tooltips.religion",
          tooltipLink:
            "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-religious-groups/latest-release",
        },
        {
          type: "field:combobox",
          comboboxType: "single",
          name: "student.user_attributes.race_id",
          label: "form.general_information_section.race",
          useOptions: () => useFormProps().races,
          tooltipContent: "form.general_information_section.tooltips.race",
          tooltipLink:
            "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-cultural-and-ethnic-groups-ascceg/2019",
        },
        {
          type: "field:combobox",
          comboboxType: "single",
          name: "student.user_attributes.primary_language_id",
          label: "form.general_information_section.primary_language",
          useOptions: () => useFormProps().languages,
          tooltipContent: "form.general_information_section.tooltips.language",
          tooltipLink:
            "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-languages-ascl/2025",
        },
        {
          type: "field:combobox",
          comboboxType: "single",
          name: "student.user_attributes.secondary_language_id",
          label: "form.general_information_section.secondary_language",
          useOptions: () => useFormProps().languages,
          tooltipContent: "form.general_information_section.tooltips.language",
          tooltipLink:
            "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-languages-ascl/2025",
        },
        {
          type: "field:combobox",
          comboboxType: "multi",
          name: "student.user_attributes.citizenship_country_ids",
          label: "form.general_information_section.citizenship",
          useOptions: () => useFormProps().countries,
          tooltipContent: "form.general_information_section.tooltips.country",
          tooltipLink:
            "https://www.abs.gov.au/statistics/classifications/standard-australian-classification-countries-sacc/2016",
        },
        {
          type: "field:combobox",
          comboboxType: "single",
          name: "student.user_attributes.origin_country_id",
          label: "form.general_information_section.country_of_origin",
          useOptions: () => useFormProps().countries,
          tooltipContent: "form.general_information_section.tooltips.country",
          tooltipLink:
            "https://www.abs.gov.au/statistics/classifications/standard-australian-classification-countries-sacc/2016",
        },
      ],
    },
    {
      type: "section:hash",
      sectionKey: "parent_information",
      title: "form.sections.parent_information",
      properties: [
        {
          type: "field:combobox",
          name: "student.parent_one_education",
          label: "form.parent_information_section.parent_one_education",
          comboboxType: "single",
          useOptions: () =>
            useTOptions(
              "parent_education_options",
              "student_attributes_options",
            ),
        },
        {
          type: "field:combobox",
          name: "student.parent_one_occupation",
          label: "form.parent_information_section.parent_one_occupation",
          comboboxType: "single",
          useOptions: () =>
            useTOptions(
              "parent_occupation_options",
              "student_attributes_options",
            ),
        },
        {
          type: "field:combobox",
          name: "student.parent_two_education",
          label: "form.parent_information_section.parent_two_education",
          comboboxType: "single",
          useOptions: () =>
            useTOptions(
              "parent_education_options",
              "student_attributes_options",
            ),
        },

        {
          type: "field:combobox",
          name: "student.parent_two_occupation",
          label: "form.parent_information_section.parent_two_occupation",
          comboboxType: "single",
          useOptions: () =>
            useTOptions(
              "parent_occupation_options",
              "student_attributes_options",
            ),
        },
      ],
    },
    {
      type: "section:hash",
      sectionKey: "ability",
      title: "form.sections.ability",
      properties: [
        {
          type: "section:scrolling",
          sectionKey: "school_qualitative_assessment",
          title:
            "form.ability_section.subsections.school_qualitative_assessment",
          properties: [
            {
              type: "field:combobox",
              name: "student.ability_attributes.overall_qualitative_assessment",
              label:
                "form.ability_section.school_qualitative_assessment.overall_qualitative_assessment",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("grade_options", "student_attributes_options"),
            },
            {
              type: "field:textarea",
              name: "student.ability_attributes.qualitative_assessment_comment",
              label:
                "form.ability_section.school_qualitative_assessment.overall_qualitative_assessment_comments",
            },
            {
              type: "field:custom:array",
              name: "student.ability_attributes.interested_subjects_attributes",
              defaultValue: { subject_id: null, qualitative_assessment: null },
              label:
                "form.ability_section.school_qualitative_assessment.interested_subjects.index_title",
              addLabel:
                "form.ability_section.school_qualitative_assessment.interested_subjects.add_button",
              removeLabel:
                "form.ability_section.school_qualitative_assessment.interested_subjects.remove_label",
              properties: [
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  useOptions: () => useFormProps().subjects,
                  name: "subject_id",
                  label:
                    "form.ability_section.school_qualitative_assessment.interested_subjects.subject_id",
                },
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  useOptions: () =>
                    useTOptions("grade_options", "student_attributes_options"),
                  name: "qualitative_assessment",
                  label:
                    "form.ability_section.school_qualitative_assessment.interested_subjects.qualitative_assessment",
                },
              ],
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "naplan_score",
          title: "form.ability_section.subsections.naplan_score",
          properties: [
            {
              type: "field:combobox",
              name: "student.ability_attributes.naplan_score_numeracy",
              label: "form.ability_section.naplan.naplan_score_numeracy",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("naplan_options", "student_attributes_options"),
            },
            {
              type: "field:combobox",
              name: "student.ability_attributes.naplan_score_reading",
              label: "form.ability_section.naplan.naplan_score_reading",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("naplan_options", "student_attributes_options"),
            },
            {
              type: "field:combobox",
              name: "student.ability_attributes.naplan_score_writing",
              label: "form.ability_section.naplan.naplan_score_writing",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("naplan_options", "student_attributes_options"),
            },
            {
              type: "field:combobox",
              name: "student.ability_attributes.naplan_score_spelling",
              label: "form.ability_section.naplan.naplan_score_spelling",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("naplan_options", "student_attributes_options"),
            },
            {
              type: "field:combobox",
              name: "student.ability_attributes.naplan_score_grammar",
              label: "form.ability_section.naplan.naplan_score_grammar",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("naplan_options", "student_attributes_options"),
            },
            {
              type: "field:attachment",
              name: "student_attachments.naplan_chart.attachment",
              accept: UPLOAD_FILE_TYPE.IMAGE_AND_PDF,
              label: "form.ability_section.naplan.naplan_chart",
              useAttachment: () =>
                useFormProps().student_attachments.naplan_chart,
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "gat",
          title: "form.ability_section.subsections.gat",
          properties: [
            {
              type: "field:radio",
              name: "student.ability_attributes.gat_attributes.writing_met_standard_score",
              label: "form.ability_section.gat.writing_met_standard_score",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "student.ability_attributes.gat_attributes.writing_achieved_excellence",
              label: "form.ability_section.gat.writing_achieved_excellence",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "student.ability_attributes.gat_attributes.reading_met_standard_score",
              label: "form.ability_section.gat.reading_met_standard_score",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "student.ability_attributes.gat_attributes.reading_achieved_excellence",
              label: "form.ability_section.gat.reading_achieved_excellence",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "student.ability_attributes.gat_attributes.numeracy_met_standard_score",
              label: "form.ability_section.gat.numeracy_met_standard_score",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:radio",
              name: "student.ability_attributes.gat_attributes.numeracy_achieved_excellence",
              label: "form.ability_section.gat.numeracy_achieved_excellence",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:input",
              name: "student.ability_attributes.gat_attributes.written_score",
              label: "form.ability_section.gat.written_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.gat_attributes.numeracy_score",
              label: "form.ability_section.gat.numeracy_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.gat_attributes.reading_score",
              label: "form.ability_section.gat.reading_score",
              inputType: "number",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "wais_iv",
          title: "form.ability_section.subsections.wais_iv",
          infoContentKey: "form.ability_section.wais_iv.tooltips.section",
          infoContentLinks: {
            link1:
              "https://www.pearsonclinical.com.au/en-au/Store/Professional-Assessments/Cognition-%26-Neuro/Wechsler-Adult-Intelligence-Scale---Fourth-Edition%3A-Australian-and-New-Zealand-Language-Adapted-Edition/p/P100010001",
            link2:
              "https://www.pearsonassessments.com/content/dam/school/global/clinical/us/assets/wais-iv/wais-iv-score-report.pdf",
          },
          properties: [
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_full_scale_iq_index",
              label: "form.ability_section.wais_iv.wais_iv_full_scale_iq_index",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_full_scale_iq_percentile_rank",
              label:
                "form.ability_section.wais_iv.wais_iv_full_scale_iq_percentile_rank",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_general_ability_index",
              label:
                "form.ability_section.wais_iv.wais_iv_general_ability_index",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_general_ability_percentile_rank",
              label:
                "form.ability_section.wais_iv.wais_iv_general_ability_percentile_rank",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_verbal_comprehension_index",
              label:
                "form.ability_section.wais_iv.wais_iv_verbal_comprehension_index",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_verbal_comprehension_percentile_rank",
              label:
                "form.ability_section.wais_iv.wais_iv_verbal_comprehension_percentile_rank",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_perceptual_reasoning_index",
              label:
                "form.ability_section.wais_iv.wais_iv_perceptual_reasoning_index",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_perceptual_reasoning_percentile_rank",
              label:
                "form.ability_section.wais_iv.wais_iv_perceptual_reasoning_percentile_rank",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_working_memory_index",
              label:
                "form.ability_section.wais_iv.wais_iv_working_memory_index",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_working_memory_percentile_rank",
              label:
                "form.ability_section.wais_iv.wais_iv_working_memory_percentile_rank",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_processing_speed_index",
              label:
                "form.ability_section.wais_iv.wais_iv_processing_speed_index",
            },
            {
              type: "field:input",
              inputType: "number",
              name: "student.ability_attributes.wais_iv_processing_speed_percentile_rank",
              label:
                "form.ability_section.wais_iv.wais_iv_processing_speed_percentile_rank",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "wisc_v",
          title: "form.ability_section.subsections.wisc_v",
          infoContentKey: "form.ability_section.wisc_v.tooltips.section",
          infoContentLinks: {
            link1:
              "https://www.pearsonclinical.com.au/en-au/Store/Professional-Assessments/Cognition-%26-Neuro/Wechsler-Intelligence-Scale-for-Children%2C-Fifth-Edition%3A-Australian-and-New-Zealand-Standardised-Edition/p/P100010200",
            link2:
              "https://www.pearsonclinical.com.au/content/dam/school/global/clinical/au/assets/wisc-v/wisc-v-interpretive-sample-report.pdf",
          },
          properties: [
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.exam_id",
              label: "form.ability_section.wisc_v.exam_id",
              inputType: "text",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.examiner_name",
              label: "form.ability_section.wisc_v.examiner_name",
              inputType: "text",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.test_date",
              label: "form.ability_section.wisc_v.test_date",
              inputType: "date",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.report_date",
              label: "form.ability_section.wisc_v.report_date",
              inputType: "date",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.grade",
              label: "form.ability_section.wisc_v.grade",
              inputType: "text",
            },
            {
              type: "field:combobox",
              name: "student.ability_attributes.wisc_v_attributes.handedness",
              label: "form.ability_section.wisc_v.handedness",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("handedness", "student_attributes_options"),
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.fsiq_score",
              label: "form.ability_section.wisc_v.fsiq_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.fsiq_percentile",
              label: "form.ability_section.wisc_v.fsiq_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.fri_score",
              label: "form.ability_section.wisc_v.fri_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.fri_percentile",
              label: "form.ability_section.wisc_v.fri_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.vci_score",
              label: "form.ability_section.wisc_v.vci_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.vci_percentile",
              label: "form.ability_section.wisc_v.vci_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.vsi_score",
              label: "form.ability_section.wisc_v.vsi_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.vsi_percentile",
              label: "form.ability_section.wisc_v.vsi_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.wmi_score",
              label: "form.ability_section.wisc_v.wmi_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.wmi_percentile",
              label: "form.ability_section.wisc_v.wmi_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.psi_score",
              label: "form.ability_section.wisc_v.psi_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wisc_v_attributes.psi_percentile",
              label: "form.ability_section.wisc_v.psi_percentile",
              inputType: "number",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "wiat_iv",
          title: "form.ability_section.subsections.wiat_iv",
          infoContentKey: "form.ability_section.wiat_iv.tooltips.section",
          infoContentLinks: {
            link1:
              "https://www.pearsonassessments.com/store/usassessments/en/Store/Professional-Assessments/Academic-Learning/Wechsler-Individual-Achievement-Test-%7C-Fourth-Edition/p/100002039.html",
            link2:
              "https://strategicpsychology.com.au/resources/articles/difference-wisc-wiat/",
            link3:
              "https://www.pearsonassessments.com/content/dam/school/global/clinical/us/assets/wiat-4/wiat-4-sample-parent-report.pdf",
          },
          properties: [
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.exam_id",
              label: "form.ability_section.wiat_iv.exam_id",
              inputType: "text",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.examiner_name",
              label: "form.ability_section.wiat_iv.examiner_name",
              inputType: "text",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.test_date",
              label: "form.ability_section.wiat_iv.test_date",
              inputType: "date",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.report_date",
              label: "form.ability_section.wiat_iv.report_date",
              inputType: "date",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.age_at_test",
              label: "form.ability_section.wiat_iv.age_at_test",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.semester",
              label: "form.ability_section.wiat_iv.semester",
              inputType: "number",
            },
            {
              type: "field:radio",
              name: "student.ability_attributes.wiat_iv_attributes.is_retest",
              label: "form.ability_section.wiat_iv.is_retest",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.grade",
              label: "form.ability_section.wiat_iv.grade",
              inputType: "text",
            },
            {
              type: "field:combobox",
              name: "student.ability_attributes.wiat_iv_attributes.handedness",
              label: "form.ability_section.wiat_iv.handedness",
              comboboxType: "single",
              useOptions: () =>
                useTOptions("handedness", "student_attributes_options"),
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.total_achievement_composite_score",
              label:
                "form.ability_section.wiat_iv.total_achievement_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.total_achievement_composite_percentile",
              label:
                "form.ability_section.wiat_iv.total_achievement_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.word_reading_score",
              label: "form.ability_section.wiat_iv.word_reading_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.word_reading_percentile",
              label: "form.ability_section.wiat_iv.word_reading_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.reading_comprehension_score",
              label: "form.ability_section.wiat_iv.reading_comprehension_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.reading_comprehension_percentile",
              label:
                "form.ability_section.wiat_iv.reading_comprehension_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.spelling_score",
              label: "form.ability_section.wiat_iv.spelling_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.spelling_percentile",
              label: "form.ability_section.wiat_iv.spelling_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.sentence_composition_score",
              label: "form.ability_section.wiat_iv.sentence_composition_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.sentence_composition_percentile",
              label:
                "form.ability_section.wiat_iv.sentence_composition_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.math_problem_solving_score",
              label: "form.ability_section.wiat_iv.math_problem_solving_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.math_problem_solving_percentile",
              label:
                "form.ability_section.wiat_iv.math_problem_solving_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.numerical_operations_score",
              label: "form.ability_section.wiat_iv.numerical_operations_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.numerical_operations_percentile",
              label:
                "form.ability_section.wiat_iv.numerical_operations_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.reading_composite_score",
              label: "form.ability_section.wiat_iv.reading_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.reading_composite_percentile",
              label:
                "form.ability_section.wiat_iv.reading_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.written_expression_composite_score",
              label:
                "form.ability_section.wiat_iv.written_expression_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.written_expression_composite_percentile",
              label:
                "form.ability_section.wiat_iv.written_expression_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.mathematics_composite_score",
              label: "form.ability_section.wiat_iv.mathematics_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.mathematics_composite_percentile",
              label:
                "form.ability_section.wiat_iv.mathematics_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.basic_reading_composite_score",
              label:
                "form.ability_section.wiat_iv.basic_reading_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.basic_reading_composite_percentile",
              label:
                "form.ability_section.wiat_iv.basic_reading_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.decoding_composite_score",
              label: "form.ability_section.wiat_iv.decoding_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.decoding_composite_percentile",
              label:
                "form.ability_section.wiat_iv.decoding_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.reading_fluency_composite_score",
              label:
                "form.ability_section.wiat_iv.reading_fluency_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.reading_fluency_composite_percentile",
              label:
                "form.ability_section.wiat_iv.reading_fluency_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.math_fluency_composite_score",
              label:
                "form.ability_section.wiat_iv.math_fluency_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.math_fluency_composite_percentile",
              label:
                "form.ability_section.wiat_iv.math_fluency_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.writing_fluency_composite_score",
              label:
                "form.ability_section.wiat_iv.writing_fluency_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.writing_fluency_composite_percentile",
              label:
                "form.ability_section.wiat_iv.writing_fluency_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.oral_language_composite_score",
              label:
                "form.ability_section.wiat_iv.oral_language_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.oral_language_composite_percentile",
              label:
                "form.ability_section.wiat_iv.oral_language_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.phonological_processing_composite_score",
              label:
                "form.ability_section.wiat_iv.phonological_processing_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.phonological_processing_composite_percentile",
              label:
                "form.ability_section.wiat_iv.phonological_processing_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.orthographic_processing_composite_score",
              label:
                "form.ability_section.wiat_iv.orthographic_processing_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.orthographic_processing_composite_percentile",
              label:
                "form.ability_section.wiat_iv.orthographic_processing_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.orthographic_processing_extended_composite_score",
              label:
                "form.ability_section.wiat_iv.orthographic_processing_extended_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.orthographic_processing_extended_composite_percentile",
              label:
                "form.ability_section.wiat_iv.orthographic_processing_extended_composite_percentile",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.dyslexia_index_composite_score",
              label:
                "form.ability_section.wiat_iv.dyslexia_index_composite_score",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.wiat_iv_attributes.dyslexia_index_composite_percentile",
              label:
                "form.ability_section.wiat_iv.dyslexia_index_composite_percentile",
              inputType: "number",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "aeas",
          title: "form.ability_section.subsections.aeas",
          infoContentKey: "form.ability_section.aeas.tooltips.section",
          infoContentLinks: {
            link1: "http://aeas.com.au/home/welcome/",
            link2: "https://www.aeas.com.au/aeas-report/",
          },
          properties: [
            {
              type: "field:input",
              name: "student.ability_attributes.aeas_score_reading",
              label: "form.ability_section.aeas.aeas_score_reading",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.aeas_score_spelling",
              label: "form.ability_section.aeas.aeas_score_spelling",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.aeas_score_written_essay",
              label: "form.ability_section.aeas.aeas_score_written_essay",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.aeas_score_listening",
              label: "form.ability_section.aeas.aeas_score_listening",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.aeas_score_speaking",
              label: "form.ability_section.aeas.aeas_score_speaking",
              inputType: "number",
            },
            {
              type: "field:input",
              name: "student.ability_attributes.aeas_score_overall",
              label: "form.ability_section.aeas.aeas_score_overall",
              inputType: "number",
            },
          ],
        },
      ],
    },
    {
      type: "section:hash",
      sectionKey: "learning_styles",
      title: "form.sections.learning_styles",
      properties: [
        {
          type: "section:scrolling",
          title:
            "form.learning_style_section.subsections.learning_style_description",
          sectionKey: "learning_style_description",
          properties: [
            {
              type: "field:textarea",
              name: "student.learning_style_description",
              label:
                "form.learning_style_section.subsections.learning_style_description",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "vark",
          title: "form.learning_style_section.subsections.vark",
          infoContentKey: "form.learning_style_section.tooltips.vark.section",
          infoContentLinks: {
            link1: "https://vark-learn.com/",
          },
          properties: [
            {
              type: "field:radioTable",
              name: "student.learning_style_vark",
              label: "form.learning_style_section.subsections.vark",
              useOptions: () =>
                useTOptions("extent_options", "student_attributes_options"),
              useRows: () =>
                useTOptions("form.learning_style_section.vark", "students"),
              rowTooltipsArgs: [
                "form.learning_style_section.tooltips.vark.rows",
                "students",
              ],
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "gardners_multiple_intelligences",
          title:
            "form.learning_style_section.subsections.gardners_multiple_intelligences",
          infoContentKey:
            "form.learning_style_section.tooltips.gardners_multiple_intelligences.section",
          infoContentLinks: {
            link1:
              "https://www.businessballs.com/self-awareness/howard-gardners-multiple-intelligences/#multiple%20intelligences%20tests",
          },
          properties: [
            {
              type: "field:radioTable",
              name: "student.learning_style_gardners_multiple_intelligences",
              label:
                "form.learning_style_section.subsections.gardners_multiple_intelligences",
              useOptions: () =>
                useTOptions("extent_options", "student_attributes_options"),
              useRows: () =>
                useTOptions(
                  "form.learning_style_section.gardners_multiple_intelligences",
                  "students",
                ),
              rowTooltipsArgs: [
                "form.learning_style_section.tooltips.gardners_multiple_intelligences.rows",
                "students",
              ],
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "kolbs_experiential_learning_theory",
          title:
            "form.learning_style_section.subsections.kolbs_experiential_learning_theory",
          infoContentKey:
            "form.learning_style_section.tooltips.kolbs_experiential_learning_theory.section",
          infoContentLinks: {
            link1:
              "https://www.growthengineering.co.uk/kolb-experiential-learning-theory/",
            link2:
              "https://practera.com/what-is-the-experiential-learning-theory-of-david-kolb/#:~:text=Kolb's%20theory%20explains%20that%20concrete,for%20students%2C%20educators%20and%20employers.",
          },
          properties: [
            {
              type: "field:radioTable",
              name: "student.learning_style_kolbs_experiential_learning_theory",
              label:
                "form.learning_style_section.subsections.kolbs_experiential_learning_theory",
              useOptions: () =>
                useTOptions("extent_options", "student_attributes_options"),
              useRows: () =>
                useTOptions(
                  "form.learning_style_section.kolbs_experiential_learning_theory",
                  "students",
                ),
              rowTooltipsArgs: [
                "form.learning_style_section.tooltips.kolbs_experiential_learning_theory.rows",
                "students",
              ],
            },
          ],
        },
      ],
    },
    {
      type: "section:hash",
      sectionKey: "learning_preferences",
      title: "form.sections.learning_preferences",
      properties: [
        {
          type: "field:textarea",
          name: "student.learning_preference_description",
          label:
            "form.learning_preference_section.learning_preference_description",
        },
        {
          type: "field:radio",
          name: "student.learning_preference_group",
          label: "form.learning_preference_section.learning_preference_group",
          useOptions: () => useTOptions("boolean", "common"),
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_group",
        },
        {
          type: "field:radio",
          name: "student.learning_preference_individual",
          label:
            "form.learning_preference_section.learning_preference_individual",
          useOptions: () => useTOptions("boolean", "common"),
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_individual",
        },
        {
          type: "field:radio",
          name: "student.learning_preference_like_students",
          label:
            "form.learning_preference_section.learning_preference_like_students",
          useOptions: () => useTOptions("boolean", "common"),
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_like_students",
        },
        {
          type: "field:radio",
          name: "student.learning_preference_diverse_students",
          label:
            "form.learning_preference_section.learning_preference_diverse_students",
          useOptions: () => useTOptions("boolean", "common"),
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_diverse_students",
        },
        {
          type: "field:slider",
          name: "student.learning_preference_teacher_centered",
          label:
            "form.learning_preference_section.learning_preference_teacher_centered",
          min: 0,
          max: 10,
          step: 1,
          lowerBoundsLabel:
            "form.learning_preference_section.bounds.teacher_centered_lower",
          upperBoundsLabel:
            "form.learning_preference_section.bounds.teacher_centered_upper",
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_teacher_centered",
        },
        {
          type: "field:slider",
          name: "student.learning_preference_structured",
          label:
            "form.learning_preference_section.learning_preference_structured",
          min: 0,
          max: 10,
          step: 1,
          lowerBoundsLabel:
            "form.learning_preference_section.bounds.structured_lower",
          upperBoundsLabel:
            "form.learning_preference_section.bounds.structured_upper",
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_structured",
        },
        {
          type: "field:slider",
          name: "student.learning_preference_technology_enhanced",
          label:
            "form.learning_preference_section.learning_preference_technology_enhanced",
          min: 0,
          max: 10,
          step: 1,
          lowerBoundsLabel:
            "form.learning_preference_section.bounds.technology_enhanced_lower",
          upperBoundsLabel:
            "form.learning_preference_section.bounds.technology_enhanced_upper",
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_technology_enhanced",
        },
        {
          type: "field:slider",
          name: "student.learning_preference_by_doing",
          label:
            "form.learning_preference_section.learning_preference_by_doing",
          min: 0,
          max: 10,
          step: 1,
          lowerBoundsLabel:
            "form.learning_preference_section.bounds.by_doing_lower",
          upperBoundsLabel:
            "form.learning_preference_section.bounds.by_doing_upper",
          tooltipContent:
            "form.learning_preference_section.tooltips.learning_preference_by_doing",
        },
      ],
    },
    {
      type: "section:hash",
      sectionKey: "other_information",
      title: "form.sections.other_informations",
      properties: [
        {
          type: "section:scrolling",
          sectionKey: "eyesight",
          title: "form.other_information_section.subsections.eyesight",
          properties: [
            {
              type: "field:combobox",
              comboboxType: "single",
              name: "student.other_information_attributes.eyesight",
              label: "form.other_information_section.subsections.eyesight",
              useOptions: () =>
                useTOptions(
                  "visual_impairment_options",
                  "student_attributes_options",
                ),
              tooltipContent:
                "form.other_information_section.tooltips.eyesight",
              tooltipLink: "https://icd.who.int/browse10/2019/en#/H53-H54",
            },
            {
              type: "field:radio",
              name: "student.other_information_attributes.eyesight_professional_diagnosis",
              label: "form.other_information_section.professional_diagnosis",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:textarea",
              name: "student.other_information_attributes.eyesight_comment",
              label: "form.other_information_section.eyesight.comments",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "adhd",
          title: "form.other_information_section.subsections.adhd",
          properties: [
            {
              type: "field:combobox",
              comboboxType: "single",
              name: "student.other_information_attributes.adhd",
              label: "form.other_information_section.subsections.adhd",
              useOptions: () =>
                useTOptions("adhd_options", "student_attributes_options"),
              tooltipContent: "form.other_information_section.tooltips.adhd",
              tooltipLink: "https://icd.who.int/browse10/2019/en#/F90-F98",
            },
            {
              type: "field:radio",
              name: "student.other_information_attributes.adhd_professional_diagnosis",
              label: "form.other_information_section.professional_diagnosis",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:textarea",
              name: "student.other_information_attributes.adhd_comment",
              label: "form.other_information_section.adhd.comments",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "autism",
          title: "form.other_information_section.subsections.autism",
          properties: [
            {
              type: "field:combobox",
              comboboxType: "single",
              name: "student.other_information_attributes.autistic",
              label: "form.other_information_section.subsections.autism",
              useOptions: () =>
                useTOptions("autism_options", "student_attributes_options"),
              tooltipContent: "form.other_information_section.tooltips.autism",
              tooltipLink: "https://icd.who.int/browse10/2019/en#/F84",
            },
            {
              type: "field:radio",
              name: "student.other_information_attributes.autistic_professional_diagnosis",
              label: "form.other_information_section.professional_diagnosis",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:textarea",
              name: "student.other_information_attributes.autistic_comment",
              label: "form.other_information_section.autism.comments",
            },
          ],
        },
        {
          type: "section:scrolling",
          sectionKey: "depression",
          title: "form.other_information_section.subsections.depression",
          properties: [
            {
              type: "field:combobox",
              comboboxType: "single",
              name: "student.other_information_attributes.depression",
              label: "form.other_information_section.subsections.depression",
              useOptions: () =>
                useTOptions("depression_options", "student_attributes_options"),
              tooltipContent:
                "form.other_information_section.tooltips.depression",
              tooltipLink: "https://icd.who.int/browse10/2019/en#F33",
            },
            {
              type: "field:radio",
              name: "student.other_information_attributes.depression_professional_diagnosis",
              label: "form.other_information_section.professional_diagnosis",
              useOptions: () => useTOptions("boolean", "common"),
            },
            {
              type: "field:textarea",
              name: "student.other_information_attributes.depression_comment",
              label: "form.other_information_section.depression.comments",
            },
          ],
        },
      ],
    },
  ],
}
